import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }

// ============================================================================
// JingleCard — premium pine-surface card
// ----------------------------------------------------------------------------
// A convenience wrapper that applies the `jingle-card-pine` surface to the
// base `Card`. Replaces hand-written `<Card className="jingle-card-pine ...">` 
// calls across the app so the premium vocabulary has a named export.
//
// Use JingleCard for:
//   - Any content card that lives inside AppAtmosphere (signed-in screens).
//   - Sub-section data panels that are NOT empty/loading states (use
//     JingleStoryEmpty / JingleStoryLoading from AppAtmosphere for those).
//   - Recap stat panels, hunt rows, admin data tables.
//
// Do NOT use JingleCard for:
//   - The top-level hero/background surface (AppHero / AppAtmosphere own that).
//   - Parchment letter surfaces (JingleParchmentLetter / ParchmentStoryCard).
//   - Guidance and notice cards (GuidanceCard / WorkshopNotice / HelpHintCard).
// ============================================================================
const JingleCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <Card
    ref={ref}
    className={cn("jingle-card-pine border-0 shadow-none", className)}
    {...props}
  />
))
JingleCard.displayName = "JingleCard"

export { JingleCard }
